import { actions, loadingState, failedState, successState } from "@grandvisionhq/state";
import { getActiveVariant } from "../../../../helpers.js";
import { startPrescriptionGlassesConfiguration } from "../../../state/actions.js";
import { fetchLensTypes, fetchLensTypesFailed, fetchLensTypesSuccess, fetchPackages, fetchPackagesFailed, fetchPackagesSuccess, fetchAddOns, fetchAddOnsFailed, fetchAddOnsSuccess } from "./actions.js";
const extractSkus = (attributeName) => (variant) => {
  const attribute = variant.attributes?.find((a) => a.name === attributeName);
  if (!attribute) {
    return /* @__PURE__ */ new Set();
  }
  if (Array.isArray(attribute.value)) {
    return new Set(attribute.value);
  } else if (typeof attribute.value === "string") {
    return new Set(attribute.value.split(","));
  }
  return /* @__PURE__ */ new Set();
};
const extractLensTypeSkus = extractSkus("compatibleLensTypeProducts");
const extractPackageSkus = extractSkus("compatiblePackageProducts");
const extractAddOnSkus = extractSkus("compatibleOptionProduct");
const extractOpticalParameters = (variant) => {
  if (!variant.opticalParameters) {
    return {};
  }
  return Object.entries(variant.opticalParameters).reduce(
    (m, [name, value]) => ({
      ...m,
      [name]: {
        values: value ? new Set(value.values) : /* @__PURE__ */ new Set()
      }
    }),
    {}
  );
};
const indexVariantsBySku = (products) => products.reduce(
  (m, p) => [
    ...m,
    [p.masterVariant.sku, p.masterVariant],
    ...p.variants.map((v) => [v.sku, v])
  ],
  []
).filter((v) => !!v[0]).reduce(
  (acc, [sku, v]) => ({
    ...acc,
    [sku]: v
  }),
  {}
);
const createStates = (skus, stateCreator) => [...skus].reduce((acc, sku) => {
  const state = stateCreator(sku);
  if (!state) {
    return acc;
  }
  return {
    ...acc,
    [sku]: state
  };
}, {});
const initialState = {
  lensTypes: {},
  packages: {},
  addOns: {},
  frames: {}
};
const reducer = actions.reducer(
  initialState,
  /**
   * Initializes a new configuration when necessary
   */
  actions.on(
    startPrescriptionGlassesConfiguration,
    (s, { payload: { frame, frameSku } }) => {
      const variant = getActiveVariant(frame, frameSku);
      return {
        ...s,
        frames: {
          [frameSku]: {
            lensTypeSkus: extractLensTypeSkus(variant),
            opticalParameters: extractOpticalParameters(frame.masterVariant)
          }
        }
      };
    }
  ),
  actions.on(
    fetchLensTypes,
    (s, a) => ({
      ...s,
      lensTypes: {
        ...s.lensTypes,
        ...createStates(a.payload.skus, () => loadingState())
      }
    })
  ),
  actions.on(
    fetchLensTypesFailed,
    (s, a) => ({
      ...s,
      lensTypes: {
        ...s.lensTypes,
        ...createStates(a.payload.skus, () => failedState(a.payload.error))
      }
    })
  ),
  actions.on(fetchLensTypesSuccess, (s, a) => {
    const variantLookup = indexVariantsBySku(a.payload.products);
    return {
      ...s,
      lensTypes: {
        ...s.lensTypes,
        ...createStates(a.payload.tried, (sku) => {
          const variant = variantLookup[sku];
          if (!variant) {
            return;
          }
          return successState({
            packageSkus: extractPackageSkus(variant),
            opticalParameters: extractOpticalParameters(variant)
          });
        }),
        ...createStates(
          a.payload.missing,
          (sku) => failedState(new Error(`Couldn't find lens type with SKU "${sku}"`))
        )
      }
    };
  }),
  actions.on(
    fetchPackages,
    (s, { payload: { skus } }) => ({
      ...s,
      packages: {
        ...s.packages,
        ...createStates(skus, () => loadingState())
      }
    })
  ),
  actions.on(
    fetchPackagesFailed,
    (s, a) => ({
      ...s,
      packages: {
        ...s.packages,
        ...createStates(a.payload.skus, () => failedState(a.payload.error))
      }
    })
  ),
  actions.on(
    fetchPackagesSuccess,
    (s, a) => ({
      ...s,
      packages: {
        ...s.packages,
        ...createStates(a.payload.tried, (sku) => {
          const product = a.payload.products.find((p) => p.masterVariant.sku === sku);
          if (!product) {
            return;
          }
          return successState({
            addOnSkus: extractAddOnSkus(product.masterVariant)
          });
        }),
        ...createStates(
          a.payload.missing,
          (sku) => failedState(new Error(`Couldn't find package with SKU "${sku}"`))
        )
      }
    })
  ),
  actions.on(
    fetchAddOns,
    (s, { payload: { skus } }) => ({
      ...s,
      addOns: {
        ...s.addOns,
        ...createStates(skus, () => loadingState())
      }
    })
  ),
  actions.on(
    fetchAddOnsFailed,
    (s, a) => ({
      ...s,
      addOns: {
        ...s.addOns,
        ...createStates(a.payload.skus, () => failedState(a.payload.error))
      }
    })
  ),
  actions.on(
    fetchAddOnsSuccess,
    (s, a) => ({
      ...s,
      addOns: {
        ...s.addOns,
        ...createStates(a.payload.tried, () => successState(void 0)),
        ...createStates(
          a.payload.missing,
          (sku) => failedState(new Error(`Couldn't find add-on with SKU "${sku}"`))
        )
      }
    })
  )
);
export {
  extractAddOnSkus,
  extractLensTypeSkus,
  extractPackageSkus,
  initialState,
  reducer
};
//# sourceMappingURL=reducer.js.map
